<template>
    <div 
        class="flow-item"
        tabindex="0"
        role="group"
        aria-label="Flow"
        :class="{ 
            'flow-item-lighter': lighter,
            'flow-item-darker': !lighter,
            'flow-item-highlight': highlight
        }"
        @pointerdown="handlePointerDown">

        <div class="flow-item-content">

            <FlowItemStack 
                v-if="hasOutputAssets"
                type="model" 
                :justify="count <= 1 ? 'center' : 'left'" 
                :assets="outputAssets" 
                :lighter="lighter"
                :limit="limit"
                @select="handleSelect" 
                @rate="handleRate" 
                @share="handleShare" 
                @download="handleDownload"
                @delete="handleDelete" />

            <FlowItemDivider 
                v-if="hasInputAssets && hasOutputAssets"
                class="flow-item-content-divider"
                :lighter="lighter" />

            <FlowItemStack 
                v-if="hasInputAssets"
                type="image"
                justify="center" 
                :assets="inputAssets" 
                :lighter="lighter"
                :limit="limit"
                @select="handleSelect" 
                @rate="handleRate" 
                @share="handleShare" 
                @download="handleDownload"
                @delete="handleDelete" />

        </div>

        <div class="flow-item-details">
            <div class="flow-item-details-options">
                <v-btn
                    class="flow-item-button flow-item-button-open"
                    variant="flat"
                    :rounded="false"
                    :elevation="0"
                    :prepend-icon="appIcon"
                    :disabled="!lastAsset"
                    aria-label="Open flow"
                    @click.stop="handleOpenFlow">
                    <span>Open flow</span>
                </v-btn>
                <v-btn
                    class="flow-item-button flow-item-button-delete"
                    variant="flat"
                    :rounded="false"
                    :elevation="0"
                    prepend-icon="fa-solid fa-trash"
                    aria-label="Delete flow"
                    @click.stop="handleDeleteFlow">
                    <span class="flow-item-delete-label">Delete flow</span>
                </v-btn>
            </div>
        </div>

    </div>
</template>

<script>
import APPS from '@generio-ai/library-legacy/constants/apps'
import DIALOGS from '@generio-ai/library-legacy/constants/dialogs'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import ROUTES from '@generio-ai/library-legacy/constants/routes'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import FlowItemStack from '@/components/library/flow/FlowItemStack.vue'
import FlowItemDivider from '@/components/library/flow/FlowItemDivider.vue'

    export default {
        name: 'FlowItem',
        components: {
            FlowItemStack,
            FlowItemDivider
        },
        props: {
            item: {
                type: Object,
                required: true
            },
            lighter: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                highlight: false
            }
        },
        computed: {
            ...mapGetters('flows', [
                'isInputAsset',
                'isOutputAsset'
            ]),

            hasInputAssets() {
                return this.inputAssets.length > 0
            },
            hasOutputAssets() {
                return this.outputAssets.length > 0
            },

            flow() {
                return this.item?.flow || 0
            },
            assets() {
                return this.item?.assets || []
            },

            count() {
                return [
                    this.hasInputAssets, this.hasOutputAssets
                ].filter(Boolean).length
            },
            limit() {
                switch (this.count) {
                    case 3: return 2
                    case 2: return 3
                    case 1: return 6
                    default: return 0
                }
            },

            inputAssets() {
                return this.assets?.filter(asset => this.isInputAsset(asset))
            },
            outputAssets() {
                return this.assets?.filter(asset => this.isOutputAsset(asset))
            },
            allAssets() {
                return [...this.inputAssets, ...this.outputAssets]
            },

            lastAsset() {
                if (this.isAgentConversation) {
                    return this.assets.reduce((latest, asset) =>
                        (asset.additional?.card?.index ?? -1) > (latest?.additional?.card?.index ?? -1) ? asset : latest, null)
                }
                if (this.allAssets.length === 0) return null
                return this.allAssets?.reduce((newest, asset) => 
                    asset.update > newest.update ? asset : newest)
            },

            isAgentConversation() {
                return this.assets?.some(asset => asset.app === APPS.AGENT)
            },
            appIcon() {
                return Object.values(ROUTES.APPSUITE)
                    .find(route => route.app === this.lastAsset?.app)?.icon || 'fa-solid fa-shapes'
            }
        },
        emits: ['select', 'rate', 'share', 'download', 'delete'],
        methods: {
            handlePointerDown(event) {
                if (event.pointerType === 'touch' && !event.target.closest('button, a, input')) {
                    event.currentTarget.focus({ preventScroll: true })
                }
            },
            ...mapActions('flows', ['openAsset']),
            handleOpenFlow() {
                if (this.lastAsset) this.openAsset({ asset: this.lastAsset })
            },
            ...mapActions('dialogs', [
                'open'
            ]),
            ...mapActions('library', [
                'deleteAssets'
            ]),
            ...mapActions('notifications', [
                'show'
            ]),
            handleSelect(asset) {
                this.$emit('select', asset)
            },
            handleRate(asset) {
                this.$emit('rate', asset)
            },
            handleShare(asset) {
                this.$emit('share', asset)
            },
            handleDownload(asset) {
                this.$emit('download', asset)
            },
            handleDelete(asset) {
                this.$emit('delete', asset)
            },
            handleDeleteFlow() {
                this.highlight = true
                this.open({
                    component: DIALOGS.CHOICE,
                    payload: {
                        title: 'Delete&nbsp;Flow',
                        message: 'Are you sure you want to delete the flow and all its assets?',
                        firstLabel: 'Cancel',
                        firstColor: 'surface',
                        onFirst: () => {
                            this.highlight = false
                        },
                        secondLabel: 'Delete',
                        secondColor: 'error',
                        onSecond: () => {
                            this.highlight = false
                            this.deleteAssets(this.assets)
                            .then(() => {
                                this.show({
                                    message: 'Flow and all its assets deleted successfully.',
                                    level: LEVELS.INFORMATION,
                                })
                            })
                            .catch(error => {
                                this.show({
                                    message: 'Failed to delete flow and its assets.',
                                    level: LEVELS.ERROR
                                })
                            })
                        }
                    }
                })
            }
        }
    }
</script>

<style scoped>
    .flow-item {
        box-sizing: border-box;
        width: 100%;
        border: 1px solid transparent;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        gap: 5px;
        padding: 10px;
        background-color: rgba(var(--v-theme-color1), 0);
        box-shadow: 0 6px 18px rgba(var(--v-theme-shadow1), 0);
        transition: background-color 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    }

    .flow-item:hover,
    .flow-item:focus-within {
        border-color: rgb(var(--v-theme-shadow1));
        background-color: rgba(var(--v-theme-color1), 0.2);
        box-shadow: 0 6px 18px rgba(var(--v-theme-shadow1), 0.6);
    }

    .flow-item:focus-visible {
        outline: 2px solid rgb(var(--v-theme-color2));
        outline-offset: -2px;
    }

    .flow-item-highlight {
        background-color: rgba(var(--v-theme-color1), 0.4);
    }

    .flow-item-content {
        width: 100%;
        flex-grow: 1;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 10px;
    }

    .flow-item-details {
        box-sizing: border-box;
        height: 52px;
        padding: 8px 12px;
        background: linear-gradient(to bottom, rgb(var(--v-theme-white)), rgb(var(--v-theme-darkerbase)));
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
        width: calc(100% + 20px);
        margin: 0 -10px -10px;
        flex-shrink: 0;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.3s ease, visibility 0s linear 0.3s;
    }

    .flow-item-details-options {
        box-sizing: border-box;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .flow-item-button {
        background-color: rgb(var(--v-theme-on-invert));
        color: rgb(var(--v-theme-invert));
        transition: background-color 120ms ease, color 120ms ease;
    }

    .flow-item-button-open:not(:disabled):is(:hover, :focus-visible) {
        background-color: rgb(var(--v-theme-color1));
        color: rgb(var(--v-theme-white));
    }

    .flow-item-button-delete:not(:disabled):is(:hover, :focus-visible) {
        background-color: rgb(var(--v-theme-error));
        color: rgb(var(--v-theme-white));
    }

    .flow-item:focus-within .flow-item-details {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
        transition-delay: 0s;
    }

    @media (hover: hover) {
        .flow-item:hover .flow-item-details {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
            transition-delay: 0s;
        }
    }

    @media (max-width: 800px) {
        .flow-item-content {
            flex-direction: column;
        }

        .flow-item-content-divider {
            display: none;
        }
    }

    @media (max-width: 600px) {
        .flow-item:not(.flow-item-highlight) {
            background-color: rgba(var(--v-theme-color1), 0.2);
        }

        .flow-item {
            border-color: rgb(var(--v-theme-shadow1));
            box-shadow: 0 6px 18px rgba(var(--v-theme-shadow1), 0.6);
        }

        .flow-item-details {
            height: 60px;
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
            transition-delay: 0s;
        }

        .flow-item-button {
            height: 44px;
        }

        .flow-item-button-delete {
            width: 44px;
            min-width: 44px;
            padding: 0;
        }

        .flow-item-delete-label {
            display: none;
        }

        .flow-item-button-delete :deep(.v-btn__prepend) {
            margin: 0;
        }
    }
</style>
